import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { assignmentsService } from '../../services';
import { CreateAssignmentRequest } from '../../types';

// Form validation schema
const assignmentSchema = z.object({
  driverId: z.string().min(1, 'Driver is required'),
  transportId: z.string().min(1, 'Transport ID is required'),
  journeyId: z.string().min(1, 'Journey ID is required'),
  date: z.string().min(1, 'Date is required'),
  scheduledStartTime: z.string().min(1, 'Start time is required'),
  scheduledEndTime: z.string().min(1, 'End time is required'),
  routeName: z.string().min(1, 'Route name is required'),
  departureStation: z.string().min(1, 'Departure station is required'),
  arrivalStation: z.string().min(1, 'Arrival station is required'),
  providerId: z.string().optional(),
  subcontractorId: z.string().optional(),
  status: z.string().min(1, 'Status is required'),
});

type AssignmentFormData = z.infer<typeof assignmentSchema>;

export const AssignmentForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<AssignmentFormData>({
    resolver: zodResolver(assignmentSchema),
  });

  // Load assignment if editing
  useEffect(() => {
    if (id) {
      const loadAssignment = async () => {
        try {
          const assignment = await assignmentsService.getAssignment(id);
          reset({
            driverId: assignment.driverId,
            transportId: assignment.transportId,
            journeyId: assignment.journeyId,
            date: assignment.date,
            scheduledStartTime: assignment.scheduledStartTime,
            scheduledEndTime: assignment.scheduledEndTime,
            routeName: assignment.routeName,
            departureStation: assignment.departureStation,
            arrivalStation: assignment.arrivalStation,
            providerId: assignment.providerId,
            subcontractorId: assignment.subcontractorId,
            status: assignment.status || 'ASSIGNED',
          });
        } catch (err) {
          setError('Failed to load assignment');
        }
      };
      loadAssignment();
    }
  }, [id, reset]);

  const onSubmit = async (data: AssignmentFormData) => {
    setIsLoading(true);
    setError(null);

    try {
      const request: CreateAssignmentRequest & { status?: string } = {
        driverId: data.driverId,
        transportId: data.transportId,
        journeyId: data.journeyId,
        date: data.date,
        scheduledStartTime: data.scheduledStartTime,
        scheduledEndTime: data.scheduledEndTime,
        routeName: data.routeName,
        departureStation: data.departureStation,
        arrivalStation: data.arrivalStation,
        providerId: data.providerId,
        subcontractorId: data.subcontractorId,
        status: data.status,
      };

      if (id) {
        await assignmentsService.updateAssignment(id, request);
      } else {
        await assignmentsService.createAssignment(request);
      }

      navigate('/assignments');
    } catch (err: any) {
      setError(err.message || 'Failed to save assignment');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">
          {id ? 'Edit Assignment' : 'New Assignment'}
        </h1>
        <p className="text-gray-600 mt-1">
          {id ? 'Update assignment details' : 'Create a new assignment'}
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="bg-white rounded-lg shadow p-8">
        {error && (
          <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Driver ID */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Driver ID</label>
            <input
              type="text"
              {...register('driverId')}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent ${
                errors.driverId ? 'border-red-500' : 'border-gray-300'
              }`}
              disabled={isLoading}
            />
            {errors.driverId && (
              <p className="text-red-500 text-sm mt-1">{errors.driverId.message}</p>
            )}
          </div>

          {/* Transport ID */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Transport ID</label>
            <input
              type="text"
              {...register('transportId')}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent ${
                errors.transportId ? 'border-red-500' : 'border-gray-300'
              }`}
              disabled={isLoading}
            />
            {errors.transportId && (
              <p className="text-red-500 text-sm mt-1">{errors.transportId.message}</p>
            )}
          </div>

          {/* Journey ID */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Journey ID</label>
            <input
              type="text"
              {...register('journeyId')}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent ${
                errors.journeyId ? 'border-red-500' : 'border-gray-300'
              }`}
              disabled={isLoading}
            />
            {errors.journeyId && (
              <p className="text-red-500 text-sm mt-1">{errors.journeyId.message}</p>
            )}
          </div>

          {/* Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
            <input
              type="date"
              {...register('date')}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent ${
                errors.date ? 'border-red-500' : 'border-gray-300'
              }`}
              disabled={isLoading}
            />
            {errors.date && <p className="text-red-500 text-sm mt-1">{errors.date.message}</p>}
          </div>

          {/* Start Time */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Start Time</label>
            <input
              type="time"
              {...register('scheduledStartTime')}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent ${
                errors.scheduledStartTime ? 'border-red-500' : 'border-gray-300'
              }`}
              disabled={isLoading}
            />
            {errors.scheduledStartTime && (
              <p className="text-red-500 text-sm mt-1">{errors.scheduledStartTime.message}</p>
            )}
          </div>

          {/* End Time */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">End Time</label>
            <input
              type="time"
              {...register('scheduledEndTime')}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent ${
                errors.scheduledEndTime ? 'border-red-500' : 'border-gray-300'
              }`}
              disabled={isLoading}
            />
            {errors.scheduledEndTime && (
              <p className="text-red-500 text-sm mt-1">{errors.scheduledEndTime.message}</p>
            )}
          </div>

          {/* Route Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Route Name</label>
            <input
              type="text"
              {...register('routeName')}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent ${
                errors.routeName ? 'border-red-500' : 'border-gray-300'
              }`}
              disabled={isLoading}
            />
            {errors.routeName && (
              <p className="text-red-500 text-sm mt-1">{errors.routeName.message}</p>
            )}
          </div>

          {/* Departure Station */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Departure Station
            </label>
            <input
              type="text"
              {...register('departureStation')}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent ${
                errors.departureStation ? 'border-red-500' : 'border-gray-300'
              }`}
              disabled={isLoading}
            />
            {errors.departureStation && (
              <p className="text-red-500 text-sm mt-1">{errors.departureStation.message}</p>
            )}
          </div>

          {/* Arrival Station */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Arrival Station
            </label>
            <input
              type="text"
              {...register('arrivalStation')}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent ${
                errors.arrivalStation ? 'border-red-500' : 'border-gray-300'
              }`}
              disabled={isLoading}
            />
            {errors.arrivalStation && (
              <p className="text-red-500 text-sm mt-1">{errors.arrivalStation.message}</p>
            )}
          </div>

          {/* Provider ID */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Provider ID</label>
            <input
              type="text"
              {...register('providerId')}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
              disabled={isLoading}
            />
          </div>

          {/* Subcontractor ID */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Subcontractor ID
            </label>
            <input
              type="text"
              {...register('subcontractorId')}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
              disabled={isLoading}
            />
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              {...register('status')}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent ${
                errors.status ? 'border-red-500' : 'border-gray-300'
              }`}
              disabled={isLoading}
            >
              <option value="ASSIGNED">Assigned</option>
              <option value="IN_PROGRESS">In Progress</option>
              <option value="COMPLETED">Completed</option>
            </select>
            {errors.status && (
              <p className="text-red-500 text-sm mt-1">{errors.status.message}</p>
            )}
          </div>
        </div>

        {/* Buttons */}
        <div className="flex gap-4 mt-8">
          <button
            type="submit"
            disabled={isLoading}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition font-medium"
          >
            {isLoading ? 'Saving...' : 'Save Assignment'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/assignments')}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-medium"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};
